import {
  isSalesforceNative,
  showPagePathPrefix,
  vizqlPrefix,
} from "./StaticConfig";

abstract class PathUtils {
  readonly hiddenSheet = "(?<hiddenSheet>[^/]+)";
  readonly workbook = "(?<workbook>[^/]+)";
  readonly view = "(?<view>[^/]+)";

  readonly vizqlRoot;
  readonly vizqlSessionId;
  constructor(pathConfig: PathConfig | null) {
    this.vizqlRoot = pathConfig?.vizqlRoot ?? vizqlPrefix(document);
    this.vizqlSessionId = pathConfig?.sessionId ?? null;
  }

  stickySession() {
    return this.vizqlSessionId ? `/sessions/${this.vizqlSessionId}` : "";
  }

  public abstract getStartSessionUrl(url: URL): URL;

  public getVizqlRoot(): string | null {
    return this.vizqlRoot;
  }
}

class VizqlPathUtils extends PathUtils {
  private readonly optionalSite = "(?<siteComponents>/t/(?<siteName>[^/]+))?";

  constructor(pathConfig: PathConfig | null) {
    super(pathConfig);
  }

  // These scenarios have no special handling for workbook or view names, and present with
  // an optional 'site' followed by a description of the request, and all relevant path components
  // after the scenario description
  private readonly genericScenario =
    "(?<scenario>" +
    "showInstantAnalytics" +
    "|authoringNewWorkbook" +
    "|editDatasource" +
    "|createPublishedDatasource" +
    "|newWorkbook" +
    "|shared" +
    "|workbooks" +
    "|show_metrics_view" +
    ")";

  private readonly sessionType = "(?<sessionType>viewing|authoring)";

  private readonly contentType =
    "(?<contentType>" +
    "workbook" +
    "|datasource" +
    "|newWorkbook" +
    "|newDatasource" +
    ")";

  // I just want this to be a dictionary with 'get' semantics
  private readonly scenarioRewrites = new Map<string, string>([
    ["authoringNewWorkbook", "newWorkbookFromDatasource"], // more descriptive Name
    ["showInstantAnalytics", "instantAnalytics"], // conceptually 'startSession' is after 'show'
    ["show_metrics_view", "showMetricsView"], // style consistency
  ]);

  // For VizQL paths to start a session based on the generic show page URL format.
  // Allow list of sources that start session can be started by.
  private static readonly VALID_FROM_SOURCES = ["fromFileUpload"];

  private rewriteScenario(scenarioName: string) {
    return this.scenarioRewrites.get(scenarioName) || scenarioName;
  }

  private adminView() {
    return new RegExp(
      `^${this.vizqlRoot!}/showadminview/${this.workbook}/${this.view}$`
    );
  }
  private views() {
    return new RegExp(
      `^${this.optionalSite}/views/${this.workbook}/${this.view}(?<customizedView>/.+/.+)?$`
    );
  }
  private authoring() {
    return new RegExp(
      `^${this.optionalSite}/authoring/${this.workbook}/${this.view}(?:/${this.hiddenSheet})?$`
    );
  }
  private authoringExtractDraft() {
    return new RegExp(
      `^${this.optionalSite}/authoringExtractDraft/${this.workbook}/${this.view}/(?<tail>.+)`
    );
  }
  private genericShowPage() {
    return new RegExp(
      `^${this.vizqlRoot!}/show${this.optionalSite}/${this.sessionType}/${
        this.contentType
      }(?:/(?<contentId>[^/]+))?(?:/(?<fromSource>from[A-Za-z]+)(?:/(?<sourceId>[^/]+))?)?$`
    );
  }
  private generic() {
    return new RegExp(
      `^(?:${this.vizqlRoot!})?${this.optionalSite}/${
        this.genericScenario
      }(?<tail>/.*)`
    );
  }

  // Helper methods to handle different startSession scenarios that use generic show page URL format
  private handleFromFileUpload(
    genericShowPageUrl: URL,
    match: RegExpExecArray
  ): void {
    if (match.groups?.contentId) {
      genericShowPageUrl.searchParams.append(":aux", match.groups.contentId);
    }
    if (match.groups?.sourceId) {
      genericShowPageUrl.searchParams.append(
        ":uploadId",
        match.groups.sourceId
      );
    }
    if (this.vizqlSessionId) {
      genericShowPageUrl.searchParams.append(":sid", this.vizqlSessionId);
    }
  }

  public getStartSessionUrl(url: URL): URL {
    const reverseProxyPattern =
      /^\/show(?<authoring>\.authoring)?(?<mobile>\.mobile)?(?<debug>\.debug)?\.html\/(?<repoUrl>.+)$/;
    const reverseProxy = reverseProxyPattern.exec(url.pathname);
    if (reverseProxy && reverseProxy.groups) {
      // Rewrite the url for the local html file to a plausible vizql /show
      // request pattern. Handlers below format the actual /startSession url
      url.pathname =
        (reverseProxy.groups.authoring ? "/authoring" : "/views") +
        (reverseProxy.groups.repoUrl ? `/${reverseProxy.groups.repoUrl}` : "");

      if (reverseProxy.groups?.mobile) {
        url.searchParams.append(":mobile", "true");
      }
      if (reverseProxy.groups?.authoring) {
        url.searchParams.append(":embed", "y");
        url.searchParams.append(":isVizPortal", "true");
      }
    }

    const viewsMatch = this.views().exec(url.pathname);
    if (viewsMatch && viewsMatch.groups) {
      const viewingUrl = new URL(url);
      viewingUrl.pathname =
        this.vizqlRoot! +
        (viewsMatch.groups.siteComponents || "") +
        `/w/${viewsMatch.groups.workbook}` +
        `/v/${viewsMatch.groups.view}` +
        "/startSession" +
        this.stickySession() +
        (viewsMatch.groups.customizedView
          ? "/customizedView" + viewsMatch.groups.customizedView
          : "/viewing");
      return viewingUrl;
    }

    const authoringMatch = this.authoring().exec(url.pathname);
    if (authoringMatch && authoringMatch.groups) {
      const authoringUrl = new URL(url);
      const authSheet = authoringMatch.groups.hiddenSheet;

      authoringUrl.pathname =
        this.vizqlRoot! +
        (authoringMatch.groups.siteComponents || "") +
        `/w/${authoringMatch.groups.workbook}` +
        `/v/${authoringMatch.groups.view}` +
        "/startSession" +
        this.stickySession() +
        "/authoring" +
        (authSheet ? `/${authSheet}` : "");
      return authoringUrl;
    }

    const authoringExtractDraftMatch = this.authoringExtractDraft().exec(
      url.pathname
    );
    if (authoringExtractDraftMatch && authoringExtractDraftMatch.groups) {
      const authoringExtractDraftUrl = new URL(url);
      authoringExtractDraftUrl.pathname =
        this.vizqlRoot! +
        (authoringExtractDraftMatch.groups.siteComponents || "") +
        `/w/${authoringExtractDraftMatch.groups.workbook}` +
        `/v/${authoringExtractDraftMatch.groups.view}` +
        "/startSession" +
        this.stickySession() +
        "/authoringExtractDraft/" +
        authoringExtractDraftMatch.groups.tail;
      return authoringExtractDraftUrl;
    }

    const genericShowPageMatch = this.genericShowPage().exec(url.pathname);
    if (genericShowPageMatch && genericShowPageMatch.groups) {
      // Validate fromSource is in allowed list
      if (
        genericShowPageMatch.groups.fromSource &&
        !VizqlPathUtils.VALID_FROM_SOURCES.includes(
          genericShowPageMatch.groups.fromSource
        )
      ) {
        throw new Error(`Unrecognized vizql URL ${url.href}`);
      }

      const genericShowPageUrl = new URL(url);
      genericShowPageUrl.pathname =
        this.vizqlRoot! +
        "/startSession" +
        (genericShowPageMatch.groups.siteComponents || "") +
        "/" +
        genericShowPageMatch.groups.sessionType +
        "/" +
        genericShowPageMatch.groups.contentType +
        (genericShowPageMatch.groups.fromSource
          ? "/" + genericShowPageMatch.groups.fromSource
          : "");

      // Handle different startSession scenarios
      if (genericShowPageMatch.groups.fromSource === "fromFileUpload") {
        if (
          !genericShowPageMatch.groups.contentId ||
          !genericShowPageMatch.groups.sourceId
        ) {
          throw new Error(`Unrecognized vizql URL ${url.href}`);
        }
        this.handleFromFileUpload(genericShowPageUrl, genericShowPageMatch);
      }

      return genericShowPageUrl;
    }

    const genericScenarioMatch = this.generic().exec(url.pathname);
    if (genericScenarioMatch && genericScenarioMatch.groups) {
      const genericUrl = new URL(url);
      const scenarioUrl = this.rewriteScenario(
        genericScenarioMatch.groups.scenario
      );
      genericUrl.pathname =
        this.vizqlRoot! +
        (genericScenarioMatch.groups.siteComponents || "") +
        "/startSession" +
        this.stickySession() +
        "/" +
        scenarioUrl +
        (genericScenarioMatch.groups.tail || "");
      return genericUrl;
    }

    const adminViewMatch = this.adminView().exec(url.pathname);
    if (adminViewMatch && adminViewMatch.groups) {
      const viewingUrl = new URL(url);
      viewingUrl.pathname =
        this.vizqlRoot! +
        (adminViewMatch.groups.siteComponents || "") +
        `/w/${adminViewMatch.groups.workbook}` +
        `/v/${adminViewMatch.groups.view}` +
        "/startSession" +
        this.stickySession() +
        "/showAdminView";
      return viewingUrl;
    }

    const localTestingPattern = /templates\/show\.([^/]*)testing\.html$/;
    if (localTestingPattern.exec(url.pathname)) {
      return new URL("/data/tsconfig.json", url.origin);
    }

    throw new Error(`Unrecognized vizql URL ${url.href}`);
  }
}

class SalesforceNativePathUtils extends PathUtils {
  private readonly showPageOptionalPrefix;

  constructor(pathConfig: PathConfig | null) {
    super(pathConfig);
    const showPagePrefix =
      pathConfig?.showPagePrefix ?? showPagePathPrefix(document);
    this.showPageOptionalPrefix = showPagePrefix
      ? `(?:${showPagePrefix})?`
      : "";
  }

  private views() {
    return new RegExp(
      `^${this.showPageOptionalPrefix}/views/${this.workbook}/${this.view}(?<customizedView>/.+/.+)?$`
    );
  }
  private authoring() {
    return new RegExp(
      `^${this.showPageOptionalPrefix}/authoring/${this.workbook}/${this.view}(?:/${this.hiddenSheet})?$`
    );
  }

  private newWorkbook() {
    return new RegExp(
      `^${this.showPageOptionalPrefix}/(?<scenario>newWorkbook)(?<tail>/.*)`
    );
  }

  private getVizqlUrl(url: URL) {
    const vizqlUrl = new URL(this.vizqlRoot!, url.origin);

    vizqlUrl.search = url.search;
    vizqlUrl.hash = url.hash;
    return vizqlUrl;
  }

  public getStartSessionUrl(url: URL): URL {
    const vizqlUrl = this.getVizqlUrl(url);

    const viewsMatch = this.views().exec(url.pathname);
    if (viewsMatch && viewsMatch.groups) {
      vizqlUrl.pathname +=
        `/w/${viewsMatch.groups.workbook}` +
        `/v/${viewsMatch.groups.view}` +
        "/startSession" +
        this.stickySession() +
        (viewsMatch.groups.customizedView
          ? "/customizedView" + viewsMatch.groups.customizedView
          : "/viewing");
      return vizqlUrl;
    }

    const authoringMatch = this.authoring().exec(url.pathname);
    if (authoringMatch && authoringMatch.groups) {
      const authSheet = authoringMatch.groups.hiddenSheet;
      vizqlUrl.pathname +=
        `/w/${authoringMatch.groups.workbook}` +
        `/v/${authoringMatch.groups.view}` +
        "/startSession" +
        this.stickySession() +
        "/authoring" +
        (authSheet ? `/${authSheet}` : "");
      return vizqlUrl;
    }

    const newWorkbookMatch = this.newWorkbook().exec(url.pathname);
    if (newWorkbookMatch && newWorkbookMatch.groups) {
      const workbookUrl = newWorkbookMatch.groups.scenario;
      vizqlUrl.pathname +=
        "/startSession" +
        this.stickySession() +
        "/" +
        workbookUrl +
        (newWorkbookMatch.groups.tail || "");
      return vizqlUrl;
    }

    throw new Error(`Unrecognized vizql URL ${url.href}`);
  }
}

export type PathConfig = {
  isSalesforceNative?: boolean;
  sessionId: string | null;
  showPagePrefix?: string;
  vizqlRoot: string | null;
};

// This generates a random number that can be appended to the string  header to try and
// distribute load for a given viz across more than 1 machine
function generateRandomPodNumber(maximum: number): string {
  return Math.floor(Math.random() * maximum).toString();
}

export class VizqlPaths {
  private static readonly DEFAULT_POD_DISTRIBUTION = 3;
  private static getPodDistribution(): number {
    // This should be configurable but when startSession is called, we only have the static showPage
    // This is also just helping distribute load in an optimal way. It is not technically required
    return VizqlPaths.DEFAULT_POD_DISTRIBUTION;
  }

  private readonly pathUtils: PathUtils;

  constructor(pathConfig: PathConfig | null) {
    const isSalesforceNativeMode =
      pathConfig?.isSalesforceNative ?? isSalesforceNative(document);
    this.pathUtils = isSalesforceNativeMode
      ? new SalesforceNativePathUtils(pathConfig)
      : new VizqlPathUtils(pathConfig);
  }

  public getStartSessionUrl(url: URL): URL {
    return this.pathUtils.getStartSessionUrl(url);
  }

  // Despite the confusing function name, this value is used by "SmartRouting" in Istio Ingress Gateway
  // to hash and route requests to specific backends
  public getVizPathHeaderValue(url: URL): string | null {
    const startSessionUrl = this.getStartSessionUrl(url);
    const vizqlRoot = this.pathUtils.getVizqlRoot();

    if (!vizqlRoot) {
      throw new Error(
        "vizqlRoot was unexpectedly null. Failed to extract viz path header value."
      );
    }

    let vizPathMatch = startSessionUrl
      .toString()
      .match(vizqlRoot + "(.*?)/v/.*?/startSession/");

    if (vizPathMatch && vizPathMatch[1]) {
      return (
        vizPathMatch[1] +
        "#" +
        generateRandomPodNumber(VizqlPaths.getPodDistribution())
      );
    }

    vizPathMatch = startSessionUrl
      .toString()
      .match(vizqlRoot + "(.*?)/startSession/");

    if (vizPathMatch && vizPathMatch[1]) {
      return (
        vizPathMatch[1] +
        "#" +
        generateRandomPodNumber(VizqlPaths.getPodDistribution())
      );
    }

    console.warn(
      `Failed to find viz path regular expression match given vizqlRoot: ${vizqlRoot} and startSessionUrl: ${startSessionUrl.href}`
    );
    return null;
  }
}
